<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte';
    import { auth } from '../js/auth.js';
    import { api } from '../js/api.js';
    import FormView from './FormView.svelte';
    import ModelView from './ModelView.svelte';
    import EmailChangeModal from './EmailChangeModal.svelte';
    import { toast } from '../js/toast.js';
    import { push } from 'svelte-spa-router';
    import { emptyTableData } from '../js/tableTypes.js';
    import type { ApiResponse } from '../js/apiTypes.js';
    import {
        AppPageHeader,
        AppLoadingState,
        AppEmptyState,
        TabBar,
        StatusBadge,
    } from './app';

    export let apiResponse = false;
    export let isPopup = false;
    export let invoiceUuid: string | null = null;
    const dispatch = createEventDispatcher();

    interface TeamPaymentPlan {
        name?: string;
    }

    interface TeamSummary {
        uuid: string;
        name?: string;
        payment_plan?: TeamPaymentPlan | null;
        is_on_trial?: boolean;
    }

    interface UserTeam {
        uuid?: string;
        role?: string;
        team?: TeamSummary;
        name?: string;
        payment_plan?: TeamPaymentPlan | null;
        is_on_trial?: boolean;
    }

    const TABS = [
        { value: 'details', label: 'My details', icon: 'user outline' },
        { value: 'teams', label: 'My teams', icon: 'users' },
    ];

    let activeTab = 'details';
    let userTeams: UserTeam[] = [];
    let teamsLoading = true;

    onMount(() => {
        window.scrollTo(0, 0);
        api.initializeCountries();
        loadUserTeams();
    });

    let showEmailChangeModal = false;

    let tableData = {
        details: {
            titleEdit: '',
            create: true,
            delete: true,
            edit: true,
            hasMediaFile: true,
            mediaFileType: 'image/*',
        },
        fields: [
            {
                key: 'first_name',
                label: 'First Name',
                type: 'text',
                editable: true,
                sortable: true,
                isMandatory: true
            },
            {
                key: 'last_name',
                label: 'Last Name',
                type: 'text',
                editable: true,
                sortable: true,
                isMandatory: true
            },
            {
                key: 'email',
                label: 'Email',
                type: 'text',
                editable: false,
                sortable: true
            },
            {
                key: 'phone',
                label: 'Mobile',
                type: 'text',
                editable: true,
                sortable: true
            },
            {
                key: 'country',
                label: 'Country',
                type: 'select',
                editable: true,
                sortable: true,
                isMandatory: true,
                mandatoryMessage: 'Please select a country',
                select: {
                    data_list: 'countries',
                    key: 'code',
                    display: 'name',
                    search_key: 'country'
                }
            }
        ]
    };

    export let displayProfile = false;
    let currentId = $auth.user.uuid;

    async function loadUserTeams() {
        teamsLoading = true;
        const res = await api.getUserTeams($auth.user.uuid) as ApiResponse<{ teams?: UserTeam[] } | UserTeam[]>;
        if (res.success && res.data) {
            const data = res.data;
            userTeams = Array.isArray(data) ? data : (data.teams ?? []);
        }
        teamsLoading = false;
    }

    async function handleSwitchTeam(teamUuid: string) {
        if (teamUuid === $auth.user.active_team_uuid) return;
        const res = await api.switchTeam($auth.user.uuid, teamUuid);
        if (res.success) {
            auth.update((state) => {
                state.user.active_team_uuid = teamUuid;
                return state;
            });
            toast.showNotification(true, 'Switched team successfully');
            window.location.reload();
        } else {
            toast.showNotification(false, res.message || 'Failed to switch team');
        }
    }

    function resolveTeam(teamEntry: UserTeam): TeamSummary {
        return teamEntry.team ?? {
            uuid: teamEntry.uuid ?? '',
            name: teamEntry.name,
            payment_plan: teamEntry.payment_plan,
            is_on_trial: teamEntry.is_on_trial,
        };
    }
</script>

<div class="profile-page" data-invoice-context={invoiceUuid || undefined}>
    <AppPageHeader
        title="My Profile"
        subtitle="Manage your account settings and preferences."
        showAction={false}
    />

    <TabBar tabs={TABS} bind:value={activeTab} on:change={(e) => (activeTab = e.detail)} />

    {#if activeTab === 'details'}
        <div class="tab-content">
            {#if isPopup}
                <ModelView
                    modelName="user"
                    bind:apiResponse
                    bind:modelId={currentId}
                    bind:displayModal={displayProfile}
                    tableData={emptyTableData}
                    on:saved={() => dispatch('saved')}
                    on:mediaDeleted={() => dispatch('saved')}
                />
            {:else}
                <FormView
                    modelName="user"
                    bind:apiResponse
                    bind:modelId={currentId}
                    bind:displayModal={displayProfile}
                    {tableData}
                    on:saved={() => dispatch('saved')}
                    on:mediaDeleted={() => dispatch('saved')}
                    on:changeEmail={() => showEmailChangeModal = true}
                />
            {/if}
        </div>
    {:else if activeTab === 'teams'}
        <div class="tab-content">
            {#if teamsLoading}
                <AppLoadingState label="Loading teams" />
            {:else if userTeams.length === 0}
                <AppEmptyState
                    icon="users"
                    title="No teams yet"
                    description="You are not a member of any teams."
                />
            {:else}
                <ul class="teams-list">
                    {#each userTeams as teamEntry}
                        {@const team = resolveTeam(teamEntry)}
                        {@const isActive = team.uuid === $auth.user.active_team_uuid}
                        <li class="team-card" class:active={isActive}>
                            <div class="team-info">
                                <div class="team-avatar" aria-hidden="true">
                                    {(team.name?.[0] || 'T').toUpperCase()}
                                </div>
                                <div class="team-details">
                                    <div class="team-name-row">
                                        <span class="team-name">{team.name}</span>
                                        {#if isActive}
                                            <StatusBadge label="Active" tone="info" />
                                        {/if}
                                    </div>
                                    <div class="team-meta">
                                        <span class="team-role">{teamEntry.role || 'member'}</span>
                                        {#if team.payment_plan}
                                            <span class="meta-sep" aria-hidden="true">·</span>
                                            <span class="team-plan">{team.payment_plan.name}</span>
                                        {:else if team.is_on_trial}
                                            <span class="meta-sep" aria-hidden="true">·</span>
                                            <StatusBadge label="Free trial" tone="warn" />
                                        {/if}
                                    </div>
                                </div>
                            </div>
                            <div class="team-actions">
                                {#if isActive}
                                    <button type="button" class="secondary-btn small" on:click={() => push('/team')}>
                                        Manage
                                    </button>
                                {:else}
                                    <button type="button" class="primary-btn small" on:click={() => handleSwitchTeam(team.uuid)}>
                                        Switch
                                    </button>
                                {/if}
                            </div>
                        </li>
                    {/each}
                </ul>
            {/if}
        </div>
    {/if}
</div>

<EmailChangeModal
    bind:isOpen={showEmailChangeModal}
    userId={currentId}
    on:close={() => showEmailChangeModal = false}
    on:emailChanged={() => {
        showEmailChangeModal = false;
        dispatch('saved');
    }}
/>

<style>
    .profile-page { padding: var(--space-5) var(--space-6); }
    @media (max-width: 768px) { .profile-page { padding: var(--space-4); } }

    .teams-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
    }

    .team-card {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-4);
        background: var(--surface);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: var(--space-4) var(--space-5);
        transition: border-color .15s, box-shadow .15s;
        flex-wrap: wrap;
    }

    .team-card:hover {
        border-color: var(--primary);
    }

    .team-card.active {
        border-color: var(--primary);
        background: var(--status-info-soft);
    }

    .team-info {
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .team-avatar {
        width: 44px;
        height: 44px;
        border-radius: var(--radius-input);
        background: var(--primary);
        color: var(--white);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: var(--text-lg);
        flex-shrink: 0;
    }

    .team-details { display: flex; flex-direction: column; gap: var(--space-1); min-width: 0; }

    .team-name-row {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        flex-wrap: wrap;
    }

    .team-name {
        font-weight: 500;
        font-size: var(--text-base);
        color: var(--grey);
    }

    .team-meta {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        flex-wrap: wrap;
        font-size: var(--text-sm);
        color: var(--grey-secondary);
    }

    .team-role { text-transform: capitalize; }
    .meta-sep { color: var(--grey-secondary); }

    .team-plan {
        padding: 1px 8px;
        border-radius: var(--radius-pill);
        background: var(--status-info-soft);
        color: var(--status-info-ink);
        font-size: var(--text-xs);
        font-weight: 500;
    }

    .team-actions {
        display: flex;
        align-items: center;
        gap: var(--space-2);
    }

    .primary-btn, .secondary-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-5);
        border-radius: var(--radius-cta);
        font: inherit;
        font-weight: 500;
        cursor: pointer;
        border: 1px solid transparent;
        transition: filter .15s, background-color .15s, border-color .15s;
    }
    .primary-btn { background: var(--primary); color: var(--white); box-shadow: var(--shadow-cta); }
    .primary-btn:hover:not(:disabled) { filter: brightness(.95); }
    .primary-btn:focus-visible { outline: none; box-shadow: var(--focus-ring); }

    .secondary-btn { background: var(--surface); color: var(--grey); border-color: var(--border-strong); }
    .secondary-btn:hover { background: var(--surface-hover); border-color: var(--primary); }
    .secondary-btn:focus-visible { outline: none; box-shadow: var(--focus-ring); }

    .small { padding: var(--space-2) var(--space-3); font-size: var(--text-sm); }

    /* Ensure form content flows naturally without scroll containers */
    .profile-page :global(.ui.profile) {
        overflow: visible !important;
        max-height: none !important;
        height: auto !important;
    }

    .profile-page :global(.ui.form) {
        overflow: visible !important;
        max-height: none !important;
    }

    /* Hide the duplicate header that shows user name */
    .profile-page :global(.ui.header:empty),
    .profile-page :global(h2.ui.header:first-of-type) {
        display: none !important;
    }

    /* Make profile image more prominent with better styling */
    .profile-page :global(.ui.image) {
        border-radius: 50% !important;
        border: 4px solid var(--primary) !important;
        box-shadow: 0 4px 12px rgba(5, 108, 242, 0.2) !important;
        cursor: pointer;
        transition: all 0.3s ease !important;
        object-fit: cover;
    }

    .profile-page :global(.ui.image:hover) {
        border-color: var(--darkprimary) !important;
        box-shadow: 0 6px 16px rgba(5, 108, 242, 0.3) !important;
        transform: scale(1.05);
    }

    /* Larger, more playful input fields */
    .profile-page :global(.ui.form input[type="text"]),
    .profile-page :global(.ui.form input[type="email"]),
    .profile-page :global(.ui.form input[type="tel"]),
    .profile-page :global(.ui.form input[type="password"]),
    .profile-page :global(.ui.form textarea),
    .profile-page :global(.ui.form select) {
        padding: 1rem 1.25rem !important;
        font-size: 1.0625rem !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 10px !important;
        transition: all 0.3s ease !important;
        background: var(--white) !important;
    }

    .profile-page :global(.ui.form input:focus),
    .profile-page :global(.ui.form textarea:focus),
    .profile-page :global(.ui.form select:focus) {
        border-color: var(--primary) !important;
        box-shadow: 0 0 0 4px rgba(5, 108, 242, 0.1) !important;
        outline: none !important;
        background: rgba(5, 108, 242, 0.02) !important;
    }

    .profile-page :global(.ui.form input:hover),
    .profile-page :global(.ui.form textarea:hover),
    .profile-page :global(.ui.form select:hover) {
        border-color: var(--secondary) !important;
    }

    /* Larger, more prominent labels */
    .profile-page :global(.ui.form label) {
        font-size: 1rem !important;
        font-weight: 500 !important;
        color: var(--grey) !important;
        margin-bottom: 0.75rem !important;
    }

    .profile-page :global(.ui.form .field) {
        margin-bottom: 1.75rem !important;
    }

    /* Make buttons larger and more colorful - Fixed sizing */
    .profile-page :global(.ui.button) {
        padding: 0.875rem 1.75rem !important;
        font-size: 1.0625rem !important;
        font-weight: 500 !important;
        border-radius: 10px !important;
        transition: all 0.3s ease !important;
        border: 0 !important;
        min-width: 140px !important;
        white-space: nowrap !important;
    }

    /* Button container - ensure consistent layout */
    .profile-page :global(.form-actions) {
        display: flex !important;
        gap: 12px !important;
        flex-wrap: wrap !important;
    }

    .profile-page :global(.form-actions .left-buttons) {
        display: flex !important;
        gap: 12px !important;
        flex-wrap: wrap !important;
    }

    /* Edit Profile button - Blue gradient */
    .profile-page :global(.form-actions .left-buttons > .ui.button.primary.blue) {
        background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%) !important;
        color: white !important;
        box-shadow: 0 4px 12px rgba(5, 108, 242, 0.3) !important;
    }

    .profile-page :global(.form-actions .left-buttons > .ui.button.primary.blue:hover) {
        background: linear-gradient(135deg, var(--darkprimary) 0%, var(--primary) 100%) !important;
        box-shadow: 0 6px 16px rgba(5, 108, 242, 0.4) !important;
        transform: translateY(-2px);
    }

    /* Change Email button - Orange/Coral gradient */
    .profile-page :global(.ui.button.secondary),
    .profile-page :global(.form-actions .left-buttons > .ui.button:not(.primary)) {
        background: linear-gradient(135deg, #ff9a56 0%, #ff7b39 100%) !important;
        color: white !important;
        box-shadow: 0 4px 12px rgba(255, 123, 57, 0.3) !important;
        border: 0 !important;
    }

    .profile-page :global(.ui.button.secondary:hover),
    .profile-page :global(.form-actions .left-buttons > .ui.button:not(.primary):hover) {
        background: linear-gradient(135deg, #ff7b39 0%, #ff5e1a 100%) !important;
        box-shadow: 0 6px 16px rgba(255, 123, 57, 0.4) !important;
        transform: translateY(-2px);
    }

    /* Cancel button - Light gray with hover */
    .profile-page :global(.form-actions > .ui.button:not(.primary):not(.blue):not(.secondary)) {
        background: var(--white) !important;
        color: var(--grey) !important;
        border: 2px solid var(--border-color) !important;
    }

    .profile-page :global(.form-actions > .ui.button:not(.primary):not(.blue):not(.secondary):hover) {
        background: var(--greyish) !important;
        border-color: var(--grey-secondary) !important;
        transform: translateY(-1px);
    }

    @media (max-width: 768px) {
        .team-card {
            flex-direction: column;
            align-items: flex-start;
            gap: var(--space-3);
        }

        .team-actions { width: 100%; }
        .team-actions .primary-btn,
        .team-actions .secondary-btn {
            flex: 1;
            text-align: center;
        }

        .profile-page :global(.ui.form input[type="text"]),
        .profile-page :global(.ui.form input[type="email"]),
        .profile-page :global(.ui.form input[type="tel"]),
        .profile-page :global(.ui.form textarea),
        .profile-page :global(.ui.form select) {
            padding: 0.875rem 1rem !important;
            font-size: 1rem !important;
        }

        .profile-page :global(.ui.button) {
            padding: 0.75rem 1.5rem !important;
            font-size: 1rem !important;
            width: 100%;
        }
    }
</style>
